import { PACK_SUBMISSION_BULK_MAX_IDS, type PackSubmission } from '@planuze/admin-contracts';
import {
  Button,
  Checkbox,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { PackageIcon } from 'lucide-react';
import { usePackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import type { SubmissionAction } from '../../hooks/use-pack-submissions.js';
import { fmtDate } from '../../lib/format.js';
import { availableActions, submissionLabel, submissionTone } from './pack-submission-status.js';
import { actionVariant } from './submission-action-config.js';

interface Props {
  readonly items: readonly PackSubmission[];
  readonly onAction: (submission: PackSubmission, action: SubmissionAction) => void;
  readonly onRowClick: (id: string) => void;
  readonly busyId?: string | undefined;
  readonly busy: boolean;
  readonly canManage: boolean;
  readonly selectedIds: ReadonlySet<string>;
  readonly onToggle: (id: string) => void;
  readonly onToggleAll: (ids: readonly string[], checked: boolean) => void;
  readonly selectAllAriaLabel: string;
  readonly selectRowAriaLabel: (packId: string, version: string) => string;
  /** Há filtro de status ativo — diferencia "sem resultados" de "fila vazia" (ADR-0700/§6). */
  readonly hasFilters: boolean;
  readonly onClearFilters: () => void;
}

/* Fila de moderação sobre o primitive `Table` de @planuze/ui (ADR-0700). O nome do pack é um
   `<button>` (a11y por teclado) — não `<tr>` onClick, que capturaria os botões de ação. Ações
   só em status não-terminal (state-machine, ADR-0448). */
export function PackSubmissionsTable({
  items,
  onAction,
  onRowClick,
  busyId,
  busy,
  canManage,
  selectedIds,
  onToggle,
  onToggleAll,
  selectAllAriaLabel,
  selectRowAriaLabel,
  hasFilters,
  onClearFilters,
}: Props) {
  const copy = usePackSubmissionsContent();
  if (items.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<PackageIcon className="size-6" />}
        title={copy.table.noResultsTitle}
        description={copy.table.noResultsDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.table.clearFiltersButton}
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={<PackageIcon className="size-6" />}
        title={copy.table.emptyQueueTitle}
        description={copy.table.emptyQueueDescription}
      />
    );
  }
  const selectedItem = items.find((item) => selectedIds.has(item.id));
  const selectedSignature =
    selectedItem === undefined ? null : availableActions(selectedItem.status).join('|');
  /* Depois da primeira seleção, só ficam elegíveis linhas com o mesmo conjunto de transições.
     Assim o lote nunca mistura, por exemplo, start-review com approve/reject. */
  const selectableItems = items.filter((item) => {
    const actions = availableActions(item.status);
    return (
      actions.length > 0 && (selectedSignature === null || actions.join('|') === selectedSignature)
    );
  });
  const visibleSignatures = new Set(
    items
      .map((item) => availableActions(item.status))
      .filter((actions) => actions.length > 0)
      .map((actions) => actions.join('|')),
  );
  const canSelectAll = selectedSignature !== null || visibleSignatures.size <= 1;
  const selectedSelectableCount = selectableItems.filter((item) => selectedIds.has(item.id)).length;
  const selectionTarget = Math.min(selectableItems.length, PACK_SUBMISSION_BULK_MAX_IDS);
  const allSelected = selectionTarget > 0 && selectedSelectableCount >= selectionTarget;
  const selectionFull = selectedIds.size >= PACK_SUBMISSION_BULK_MAX_IDS;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {canManage && (
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                disabled={busy || selectableItems.length === 0 || !canSelectAll}
                onCheckedChange={(checked) =>
                  onToggleAll(
                    selectableItems.map((item) => item.id),
                    checked === true,
                  )
                }
                aria-label={selectAllAriaLabel}
              />
            </TableHead>
          )}
          <TableHead>{copy.fieldLabels.pack}</TableHead>
          <TableHead>{copy.fieldLabels.publisher}</TableHead>
          <TableHead>{copy.fieldLabels.status}</TableHead>
          <TableHead>{copy.fieldLabels.submittedAt}</TableHead>
          {canManage && <TableHead>{copy.fieldLabels.actions}</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((submission) => {
          const rowActions = availableActions(submission.status);
          const compatible =
            selectedSignature === null || rowActions.join('|') === selectedSignature;
          const rowSelected = selectedIds.has(submission.id);
          return (
            <TableRow key={submission.id}>
              {canManage && (
                <TableCell>
                  <Checkbox
                    checked={rowSelected}
                    disabled={
                      busy ||
                      rowActions.length === 0 ||
                      (!rowSelected && (!compatible || selectionFull))
                    }
                    onCheckedChange={() => onToggle(submission.id)}
                    aria-label={selectRowAriaLabel(submission.packId, submission.version)}
                  />
                </TableCell>
              )}
              <TableCell>
                <button
                  type="button"
                  className="font-medium text-foreground transition-colors hover:underline"
                  onClick={() => onRowClick(submission.id)}
                >
                  {submission.packId}
                </button>{' '}
                <span className="text-muted-foreground">v{submission.version}</span>
              </TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">
                {submission.publisherId}
              </TableCell>
              <TableCell>
                <StatusBadge variant={submissionTone(submission.status)} dot>
                  {submissionLabel(submission.status, copy.submissionStatusLabels)}
                </StatusBadge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {fmtDate(submission.submittedAt)}
              </TableCell>
              {canManage && (
                <TableCell>
                  {(() => {
                    const actions = rowActions;
                    if (actions.length === 0) {
                      return <span className="text-xs text-muted-foreground">—</span>;
                    }
                    return (
                      <div className="flex gap-2">
                        {actions.map((act) => (
                          <Button
                            key={act}
                            size="sm"
                            variant={actionVariant(act)}
                            loading={
                              busyId === submission.id &&
                              (act === 'start-review' || act === 'reopen')
                            }
                            disabled={busy}
                            onClick={() => onAction(submission, act)}
                          >
                            {copy.actionLabel[act]}
                          </Button>
                        ))}
                      </div>
                    );
                  })()}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
